import {AnimatePresence, motion} from "framer-motion";
import {toggleMulliganModal} from "modules/actions";
import {setForceTutorialModal} from "modules/actions/user";
import React from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {NavLink} from "react-router-dom";
import styled from "@emotion/styled";
import {css} from "@mui/material";
import {getRulesLinkAccordingState} from "modules/utils/dom_helpers";
import {getCurrentUserId, getUserStore} from "modules/selectors";
import {useNavigate} from "react-router";

const MenuList = styled(motion.ul)`
	display: flex;
	flex-flow: column;
	padding: 60px 39px 0;
	position: absolute;
	right: 0;
	max-width: 100%;
	width: 300px;
	z-index: 10;
	height: 100vh;
	overflow: auto;
	pointer-events: none;
	visibility: hidden;
	&.menu {
		pointer-events: auto;
		visibility: visible;
	}
`;

const MenuItem = styled(motion.li)`
	margin-bottom: 50px;
`;

const LinkStyles = css`
	color: #848990;
	font-size: 24px;
	text-decoration: none;
	cursor: pointer;

	&.active,
	&:hover {
		color: #fff;
	}
`;

const MenuLink = styled(NavLink)`
	${LinkStyles};
`;

MenuLink.defaultProps = {
	role: "menuitem",
};

const MenuLinkExternal = styled.a`
	${LinkStyles};
`;

MenuLinkExternal.defaultProps = {
	role: "menuitem",
};

const MenuLinkButton = styled.button`
	background: none;
	box-shadow: none;
	border: none;
	padding: 0;
	${LinkStyles};
`;

MenuLinkButton.defaultProps = {
	role: "menuitem",
};

MenuLinkExternal.defaultProps = {
	role: "menuitem",
};

const variants = {
	open: {
		transition: {staggerChildren: 0.07, delayChildren: 0.2},
	},
	closed: {
		transition: {staggerChildren: 0.05, staggerDirection: -1},
	},
};

const variantsItem = {
	open: {
		y: 0,
		opacity: 1,
		transition: {
			y: {stiffness: 1000, velocity: -100},
		},
	},
	closed: {
		y: 50,
		opacity: 0,
		transition: {
			y: {stiffness: 1000},
		},
	},
};

const Items: React.FC<IProps> = ({toggleOpen, isOpen}) => {
	const i18n = useTranslation();
	const {countryCode, stateCode} = useSelector(getUserStore);
	const userId = useSelector(getCurrentUserId);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const openMulligan = () => {
		dispatch(toggleMulliganModal());
		toggleOpen();
	};

	const openTutorial = () => {
		dispatch(setForceTutorialModal(true));
		toggleOpen();
	};

	const openProfile = () => {
		if (userId) {
			navigate(`/leaderboard/${userId}`);
		}
	};

	return (
		<MenuList variants={variants} className={isOpen ? "menu" : ""} role={"menu"}>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLink to={"/"}>{i18n.t("menu.items.play")}</MenuLink>
			</MenuItem>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLinkExternal
					aria-label="The link opens in a new tab"
					href={getRulesLinkAccordingState(countryCode, stateCode)}
					target="_blank"
				>
					{i18n.t("menu.items.terms")}
				</MenuLinkExternal>
			</MenuItem>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLinkButton onClick={openMulligan}>
					{i18n.t("menu.items.mulligan")}
				</MenuLinkButton>
			</MenuItem>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLinkButton onClick={openTutorial}>
					{i18n.t("menu.items.how_to_play")}
				</MenuLinkButton>
			</MenuItem>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLinkButton onClick={openProfile}>
					{i18n.t("menu.items.user_profile")}
				</MenuLinkButton>
			</MenuItem>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLink to={"/leaderboard"}>{i18n.t("menu.items.leaderboard")}</MenuLink>
			</MenuItem>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLink to={"/groups"}>{i18n.t("menu.items.groups", "Groups")}</MenuLink>
			</MenuItem>
			<MenuItem variants={variantsItem} whileHover={{scale: 1.1}} whileTap={{scale: 0.95}}>
				<MenuLink to={"/rewards"}>{i18n.t("menu.items.rewards", "Badges")}</MenuLink>
			</MenuItem>
		</MenuList>
	);
};

interface IProps {
	isOpen: boolean;
	toggleOpen: () => void;
}

export const Menu: React.FC<IProps> = ({isOpen, toggleOpen}) => {
	return (
		<AnimatePresence initial={true}>
			<motion.nav
				exit={{
					display: "none",
					transition: {delay: 1},
				}}
			>
				<Items isOpen={isOpen} toggleOpen={toggleOpen} />
			</motion.nav>
		</AnimatePresence>
	);
};

export default Menu;
